import { createSlice } from '@reduxjs/toolkit';
import { AdvisorCompaniesState } from './advisor-companies.state';
import { AdvisorCompany } from './models/advisor-company.model';
import { LoadAdvisorCompanies } from './thunks/load-advisor-companies.thunk';
import { CreateAdvisorCompany } from './thunks/create-advisor-company.thunk';
import { RenameAdvisorCompany } from './thunks/rename-advisor-company.thunk';
import { UpdateAdvisorCompanyText } from './thunks/update-advisor-company-text.thunk';
import { DeactivateAdvisorCompany } from './thunks/deactivate-advisor-company.thunk';
import { ActivateAdvisorCompany } from './thunks/activate-advisor-company.thunk';

const initialState: AdvisorCompaniesState = {
    companies: [],
    isLoading: false,
    status: 'loading',
    error: '',
};

const sortByName = (companies: AdvisorCompany[]) =>
    [...companies].sort((a, b) => a.name.localeCompare(b.name));

const upsert = (companies: AdvisorCompany[], updated: AdvisorCompany) =>
    sortByName(
        companies.map((company) =>
            company.id === updated.id ? updated : company
        )
    );

const advisorCompaniesSlice = createSlice({
    name: 'advisorCompanies',
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(LoadAdvisorCompanies.pending, (state) => setPendingState(state))
            .addCase(LoadAdvisorCompanies.fulfilled, (state, action) => {
                state.companies = sortByName(action.payload ?? []);
                setSucceededState(state);
            })
            .addCase(LoadAdvisorCompanies.rejected, (state) => setRejectedState(state))
            .addCase(CreateAdvisorCompany.fulfilled, (state, action) => {
                state.companies = sortByName([...state.companies, action.payload]);
            })
            .addCase(RenameAdvisorCompany.fulfilled, (state, action) => {
                state.companies = upsert(state.companies, action.payload);
            })
            .addCase(UpdateAdvisorCompanyText.fulfilled, (state, action) => {
                state.companies = upsert(state.companies, action.payload);
            })
            .addCase(DeactivateAdvisorCompany.fulfilled, (state, action) => {
                state.companies = state.companies.map((company) =>
                    company.id === action.payload
                        ? { ...company, isActive: false }
                        : company
                );
            })
            .addCase(ActivateAdvisorCompany.fulfilled, (state, action) => {
                state.companies = state.companies.map((company) =>
                    company.id === action.payload
                        ? { ...company, isActive: true }
                        : company
                );
            });
    },
});

const setPendingState = (state: AdvisorCompaniesState) => {
    state.isLoading = true;
    state.status = 'loading';
    state.error = '';
};

const setSucceededState = (state: AdvisorCompaniesState) => {
    state.isLoading = false;
    state.status = 'succeeded';
    state.error = '';
};

const setRejectedState = (state: AdvisorCompaniesState) => {
    state.isLoading = false;
    state.status = 'failed';
    state.error = 'Failed to load advisor companies';
};

export default advisorCompaniesSlice.reducer;
